import { AdditionalServiceLanguage, type ServiceLanguage } from "@albertine/i18n";
import { z } from "zod";
import { OfferingCategory } from "./offeringCategory";
import { ISODateTime } from "./primitives";

export const themeNameMaxLength = 255;

export const ThemeType = z.enum([
    "spotlight",
    "static",
    "dynamic",
    "cityGuide",
    "category",
]);
export type ThemeType = z.infer<typeof ThemeType>;

export const ThemeLocation = z.object({
    city: z.string().nullable(),
    country: z.string(),
    countryCode: z.string().max(2),
});
export type ThemeLocation = z.infer<typeof ThemeLocation>;

export function themeLocationToText(themeLocation: ThemeLocation) {
    return themeLocation.city
        ? `${themeLocation.city}, ${themeLocation.country}`
        : themeLocation.country;
}

export const ThemePreference = z.enum([
    "travel",
    "wellness",
    "dining",
    "sportsEvents",
    "culturalEvents",
    "localExperiences",
]);
export type ThemePreference = z.infer<typeof ThemePreference>;

export type ThemePreferenceScoreMap = Record<ThemePreference, number>;

export function zeroThemePreferenceScoreMap(): ThemePreferenceScoreMap {
    return {
        travel: 0,
        wellness: 0,
        dining: 0,
        sportsEvents: 0,
        culturalEvents: 0,
        localExperiences: 0,
    };
}

const ThemeGroup = z.object({ id: z.string(), name: z.string() });

export type ThemeGroup = z.infer<typeof ThemeGroup>;

export const ThemePreferenceTextMap: Record<ThemePreference, string> = {
    culturalEvents: "Cultural events",
    dining: "Dining",
    localExperiences: "Local experiences",
    travel: "Travel",
    sportsEvents: "Sports events",
    wellness: "Wellness",
};

export const ThemeTranslation = z.object({
    name: z.string(),
});
export type ThemeTranslation = z.infer<typeof ThemeTranslation>;

export const Theme = z.object({
    key: z.string(),
    name: z.string(),
    published: ISODateTime.nullable(),
    slugs: z.array(z.string()).nullable(),
    image: z.url().nullable(),
    query: z.string(),
    filter: z.string().nullable().default(null),
    type: ThemeType,
    location: z.array(ThemeLocation),
    group: ThemeGroup.nullable(),
    preferences: z.array(ThemePreference),
    translations: z.partialRecord(AdditionalServiceLanguage, ThemeTranslation),
    offeringCategories: z.array(OfferingCategory).optional(),
});

export type Theme = z.infer<typeof Theme>;

export const ThemeWithoutTranslations = Theme.omit({
    translations: true,
});

export type ThemeWithoutTranslations = z.infer<typeof ThemeWithoutTranslations>;

export const DynamicTheme = Theme;
export type DynamicTheme = Theme;
export const DynamicThemeTranslation = ThemeTranslation;
export type DynamicThemeTranslation = ThemeTranslation;
export const DynamicThemeWithoutTranslations = ThemeWithoutTranslations;
export type DynamicThemeWithoutTranslations = ThemeWithoutTranslations;
export const dynamicThemeNameMaxLength = themeNameMaxLength;

export function findThemeTranslations(
    item: Theme,
    serviceLanguage: ServiceLanguage,
): ThemeTranslation {
    const englishBaseline = {
        name: item.name,
    };

    if (serviceLanguage === "en") {
        return englishBaseline;
    }

    const translation = item?.translations?.[serviceLanguage] || undefined;

    return translation
        ? {
              name: translation.name || englishBaseline.name,
          }
        : englishBaseline;
}
